import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useIdleLogout — automatic sign-out after user inactivity.
 *
 * Fires a warning at `warnMs` since the last activity, then calls `onLogout`
 * at `timeoutMs`. Any user interaction (mousemove, keydown, click, scroll,
 * touchstart) resets both timers. Also listens for the browser regaining
 * focus after being backgrounded — a 30-minute tab in the background counts
 * as idle even though `mousemove` never fired.
 *
 * Multi-tab: activity in ANY tab resets ALL tabs via a `localStorage` beacon,
 * so the user isn't logged out mid-task in the tab they weren't touching.
 *
 * @param {object} opts
 * @param {boolean} opts.enabled — hook is a no-op when false (e.g. logged-out state)
 * @param {number}  opts.timeoutMs — total inactivity before logout (default 15 min)
 * @param {number}  opts.warnMs — show warning this many ms into the idle period
 *                                (default `timeoutMs - 30s`, giving a 30s countdown)
 * @param {() => void} opts.onLogout — called when the timeout fires
 *
 * @returns {{
 *   showWarning: boolean,        // true when the countdown modal should render
 *   secondsRemaining: number,    // ticks down every second during the warning window
 *   stayActive: () => void,      // call from "Stay signed in" button
 * }}
 */
export function useIdleLogout({
  enabled,
  timeoutMs = 15 * 60 * 1000,   // 15 minutes
  warnMs,
  onLogout,
}) {
  const warnAt = warnMs ?? (timeoutMs - 30 * 1000);  // 30-second countdown
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(
    Math.max(0, Math.round((timeoutMs - warnAt) / 1000))
  );

  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  // Storage key for cross-tab activity sync. Value = timestamp of last input.
  const STORAGE_KEY = 'dr:last-activity-ts';

  const clearAll = useCallback(() => {
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warnTimerRef.current = logoutTimerRef.current = countdownRef.current = null;
  }, []);

  const scheduleTimers = useCallback(() => {
    clearAll();
    warnTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsRemaining(Math.max(0, Math.round((timeoutMs - warnAt) / 1000)));
      // Tick a visible countdown so the user sees urgency
      countdownRef.current = setInterval(() => {
        setSecondsRemaining((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
    }, warnAt);
    logoutTimerRef.current = setTimeout(() => {
      clearAll();
      setShowWarning(false);
      if (onLogoutRef.current) onLogoutRef.current();
    }, timeoutMs);
  }, [timeoutMs, warnAt, clearAll]);

  // Public: called from "Stay signed in" button
  const stayActive = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* private mode */ }
    setShowWarning(false);
    scheduleTimers();
  }, [scheduleTimers]);

  useEffect(() => {
    if (!enabled) {
      clearAll();
      setShowWarning(false);
      return;
    }

    const onActivity = () => {
      // Debounce cross-tab broadcasts: write at most every 5s
      try {
        const last = Number(localStorage.getItem(STORAGE_KEY) || 0);
        if (Date.now() - last > 5000) {
          localStorage.setItem(STORAGE_KEY, String(Date.now()));
        }
      } catch { /* private mode */ }
      if (!showWarning) scheduleTimers();
    };

    // The warning modal has its own "Stay signed in" button, so we DELIBERATELY
    // do NOT reset the timer on activity once the warning is showing — the user
    // must explicitly acknowledge it. Prevents accidental extensions from stray
    // mouse movement while the browser is minimized.

    const onStorage = (e) => {
      // Another tab reported activity — reset our timers too, unless we're
      // already in the warning window (respect the confirm-to-continue UX).
      if (e.key === STORAGE_KEY && !showWarning) {
        scheduleTimers();
      }
    };

    const onVisibility = () => {
      // Tab regained focus after being backgrounded — if it's been long enough
      // since the last stored activity, we shouldn't reset. Let the existing
      // timers finish counting down.
      if (document.visibilityState === 'visible' && !showWarning) {
        try {
          const last = Number(localStorage.getItem(STORAGE_KEY) || 0);
          const elapsed = Date.now() - last;
          if (elapsed < warnAt) scheduleTimers();
        } catch { scheduleTimers(); }
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);

    scheduleTimers();

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
      clearAll();
    };
  }, [enabled, scheduleTimers, clearAll, warnAt, showWarning]);

  return { showWarning, secondsRemaining, stayActive };
}
